import { LegalPage, LegalSection } from "@/components/LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      testId="privacy-page"
      title="Privacy Policy"
      subtitle="This policy explains what information MovaRaBoard collects, how we use it, and the choices you have. It applies to our shipment-visibility platform and related services."
      updated="July 7, 2026"
    >
      <p>
        MovaRaBoard ("we", "us", or "our") is committed to protecting the privacy of the businesses and users
        that use our platform. This Privacy Policy describes how we collect, use, disclose, and safeguard
        information in connection with the Service.
      </p>

      <LegalSection heading="1. Information We Collect">
        <ul className="list-disc pl-5 space-y-1">
          <li><span className="font-semibold text-ink">Account information:</span> names, email addresses, roles, and workspace/company details provided during registration or invitation.</li>
          <li><span className="font-semibold text-ink">Operational data:</span> shipment records, documents, delivery details, and related logistics information you submit ("Customer Data").</li>
          <li><span className="font-semibold text-ink">Usage data:</span> log data, device and browser information, and product analytics used to operate and improve the Service.</li>
          <li><span className="font-semibold text-ink">Billing data:</span> subscription and payment metadata handled through our payment processor (we do not store full card numbers).</li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. How We Use Information">
        <p>We use information to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>provide, maintain, secure, and improve the Service;</li>
          <li>authenticate users and enforce workspace/tenant isolation;</li>
          <li>send transactional communications such as invitations and service notices;</li>
          <li>process subscriptions and billing;</li>
          <li>comply with legal obligations and prevent fraud or abuse.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. Tenant Isolation & Data Scoping">
        <p>
          Customer Data is strictly scoped to your workspace. We do not sell Customer Data, and we do not use
          it to serve advertising. Public tracking links expose only the limited delivery-relevant fields of a
          single shipment and never expose tenant lists or financial data.
        </p>
      </LegalSection>

      <LegalSection heading="4. Sharing & Sub-processors">
        <p>
          We share information only with service providers that help us operate the Service — for example,
          cloud hosting, object storage, email delivery, and payment processing — under agreements that require
          them to protect the data. We may also disclose information where required by law.
        </p>
      </LegalSection>

      <LegalSection heading="5. Data Security">
        <p>
          We implement administrative, technical, and organizational safeguards designed to protect
          information, including access controls, encryption in transit, and scoped authentication. No method
          of transmission or storage is completely secure, and we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection heading="6. Data Retention">
        <p>
          We retain Customer Data for as long as your workspace is active and as needed to provide the Service.
          Following termination, we make data available for export for a limited period before deletion, unless
          a longer retention period is required by law.
        </p>
      </LegalSection>

      <LegalSection heading="7. Your Rights">
        <p>
          Depending on your jurisdiction, you may have rights to access, correct, export, or delete personal
          information. Workspace administrators can manage most user data directly within the Service. For other
          requests, contact us using the details below.
        </p>
      </LegalSection>

      <LegalSection heading="8. International Transfers">
        <p>
          Information may be processed and stored in locations outside your country. Where we transfer personal
          information across borders, we take steps to ensure appropriate safeguards are in place.
        </p>
      </LegalSection>

      <LegalSection heading="9. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be communicated through the
          Service or by email, and the "Last updated" date above will be revised accordingly.
        </p>
      </LegalSection>

      <LegalSection heading="10. Contact">
        <p>
          For privacy questions or requests, contact{" "}
          <a href="mailto:support@movaraboard.com" className="font-semibold text-[#0B132B] underline">
            support@movaraboard.com
          </a>.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
